import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import type { SyntheticEvent } from 'react';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useInterval, useUpdate } from 'react-use';

import NavIconNotifications from 'assets/icons/nav-icons/notifications.inline.svg';

import ClearIcon from '@mui/icons-material/Clear';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import useMaskAmount from 'app-components/hooks/privacyMode/useMaskAmount';
import { alertsActions, alertsSelectors, alertsTypes } from 'app-components/flux/alerts';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { useFetchOnce } from 'app-components/hooks/utils';
import { getSmartTimeLabel } from 'app-components/utils/date';
import { isDemoMode } from 'app-components/utils/core';

import Button from '@mui/material/Button';
import ListItem from '@mui/material/ListItem';
import Popover from '@mui/material/Popover';
import type { PopoverActions } from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog';
import Dump from 'components/Dump';
import ZeroStateView from 'components/ZeroStateView';
import { useAppSelector } from 'hooks/redux';
import { useNavItemStyles } from '../commonNavStyles';

const pathForAlert = (alert: any) => {
  let path = alert.deepLinkUri;

  switch (alert.deepLinkComponent) {
    case 'transactions':
      if (alert.deepLinkEntityId) {
        const tmpURL = new URL(window.location.origin);
        tmpURL.pathname = '/transactions';
        tmpURL.searchParams.append('txnId', alert.deepLinkEntityId);
        path = tmpURL.pathname + tmpURL.search;
      }
      break;
    default:
  }

  switch (alert.type) {
    case 'UNCATEGORIZED_TXNS':
      path = '/reports/spending/by-category/total?selectCategory=0';
      break;
    case 'REFUND_TRACKER':
      path = '/upcoming/refund-tracker';
      break;
    case 'MONTHLY_SPENDING_COMPARISON':
      path = '/reports/spending/by-none/over-time';
      break;
    default:
  }

  return path;
};

const iconStyle = {
  '& .MuiBadge-badge': {
    minWidth: '16px !important',
    height: '16px !important',
    fontSize: '10px !important',
    paddingLeft: '4px !important',
    paddingRight: '4px !important',
  },
};

const useStyles = makeStyles<void, 'content' | 'listItem' | 'showOnHover' | 'hideOnHover'>()(
  (theme, _params, classes) => ({
    content: {
      minHeight: 340,
      maxHeight: 500,
      overflow: 'auto',
    },
    listItem: {
      '&:hover': {
        [`& .${classes.showOnHover}`]: {
          opacity: 1,
          visibility: 'visible',
          display: 'flex',
        },
        [`& .${classes.hideOnHover}`]: {
          opacity: 0,
          visibility: 'hidden',
          display: 'none',
        },
      },
    },
    hideOnHover: {
      opacity: 1,
      visibility: 'visible',
      display: 'flex',
    },
    showOnHover: {
      opacity: 0,
      visibility: 'hidden',
      display: 'none',
    },
  }),
);
type Props = {
  buttonRef: React.RefObject<HTMLButtonElement>;
  open: boolean;
  onClose: () => void;
};

const NotificationsNavIcon = React.memo(({ onClose, open, buttonRef }: Props) => {
  const ref = useRef<HTMLSpanElement>(null);
  const { classes } = useStyles();
  const { classes: navItemClasses } = useNavItemStyles();
  const navigate = useNavigate();
  const theme = useTheme();

  const palette = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(alertsTypes.IconBgColors).map(([_key, value], index) => [
          value,
          theme.palette.data.sequence[index % theme.palette.data.sequence.length],
        ]),
      ),
    [theme.palette.data.sequence],
  );

  const dispatch = useDispatch();
  useFetchOnce(alertsActions.getAlerts, alertsSelectors);
  const [disableClickAway, setDisableClickAway] = useState(false);
  const alertsUnread: any = useAppSelector(alertsSelectors.getAlertsUnread);
  const alerts = useAppSelector(alertsSelectors.getAlerts);
  const popoverActions = React.createRef<PopoverActions>();

  const update = useUpdate();
  useInterval(update, open ? 60000 : null);

  const onClickAway = useCallback(
    (event: any) => {
      if (buttonRef.current?.contains(event.target)) {
        event.preventDefault();
      }
      if (!disableClickAway) {
        onClose();
      }
    },
    [disableClickAway, onClose, buttonRef],
  );

  const onPopperKeyDown = useCallback(
    (event: any) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  // mark items as read when visible
  const [alertsRead, setAlertsRead] = useState(ImmutableSet<string>());
  const intersectionObserverHandler = useCallback<IntersectionObserverCallback>(
    (entries) =>
      entries
        .filter((entry) => entry.isIntersecting)
        // @ts-expect-error dataset does exist here
        .forEach((entry) => setAlertsRead((set) => set.add(entry.target.dataset.alertid))),
    [],
  );
  const [intersectionObserver, setIntersectionObserver] = useState<IntersectionObserver | undefined | null>();
  useEffect(() => {
    let observer: IntersectionObserver | undefined;
    if (open) {
      setTimeout(() => {
        observer = new IntersectionObserver(intersectionObserverHandler, {
          root: null,
          rootMargin: '0px',
          threshold: 0.9,
        });
        setIntersectionObserver(observer);
      }, 3000);
    }
    return () => {
      observer?.disconnect();
      setIntersectionObserver(null);
    };
  }, [open, intersectionObserverHandler]);

  const dispatchAlertsRead = useCallback(() => {
    if (alertsRead?.size) {
      const alertsUpdates = alertsRead
        .map((id) => alerts.get(id))
        .filter((alert): alert is NonNullable<typeof alert> => !!alert && !alert.isRead && !alert.isDismissed)
        .toArray()
        .map((alert) => alert.set('isRead', true));
      if (alertsUpdates.length) {
        dispatch(alertsActions.batchAlerts(alertsUpdates));
      }
    }
  }, [alertsRead, alerts, dispatch]);

  useEffect(() => {
    if (!open && !isDemoMode()) {
      dispatchAlertsRead();
    }
  }, [open, dispatchAlertsRead]);

  const maskAmount = useMaskAmount();

  return (
    <>
      <Badge
        ref={ref}
        max={99}
        badgeContent={alertsUnread.size}
        color='error'
        className={navItemClasses.navIconParentMargin}
        sx={iconStyle}
      >
        <NavIconNotifications className={navItemClasses.navIconNoMargin} />
      </Badge>

      {open && ref.current && (
        <Popover
          action={popoverActions}
          anchorEl={ref.current}
          open={Boolean(open)}
          onKeyDown={onPopperKeyDown}
          // className={classes.popper}
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'bottom',
          }}
          transformOrigin={{
            horizontal: 'right',
            vertical: 'top',
          }}
          slotProps={{
            paper: {
              elevation: 24,
              style: {
                width: 384,
                transform: 'translate3d(0, 0, 0)', // fixes a scroll issue: https://github.com/mui-org/@mui/material/issues/10601
                padding: 1,
              },
            },
            root: {
              onClick: onClickAway,
            },
          }}
          disableEnforceFocus
        >
          {/* Header Section */}
          <Box padding={2} display='flex' justifyContent='space-between' alignItems='baseline'>
            <Typography variant='titleMedium'>Notifications</Typography>
            <Box>
              {Boolean(alerts.size) && (
                <Button
                  id='clear-all'
                  variant='text'
                  size='small'
                  onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                    const alertsUpdates = alerts
                      .valueSeq()
                      .map((alert) =>
                        alert.merge({
                          isDismissed: true,
                          isDeleted: true,
                        }),
                      )
                      .toArray();
                    if (alertsUpdates.length > 0) {
                      setDisableClickAway(true);
                      dispatch(
                        rootUiActions.createDialog(
                          rootUiTypes.mkRootUiData({
                            id: 'clear-notifications-confirmation',
                            type: DIALOG_CONFIRMATION,
                            allowNesting: true,
                            props: ImmutableMap(
                              mkConfirmationDialogProps({
                                title: 'Clear Notifications',
                                content: 'Are you sure you want to clear all notifications?',
                                confirmLabel: 'Yes',
                                confirmAction: (reason, closeEvent) => {
                                  dispatch(
                                    alertsActions.batchAlerts(alertsUpdates, {
                                      undo: {
                                        userMessage: `${alerts.size} notifications dismissed.`,
                                        disableUndoAction: true,
                                      },
                                    }),
                                  );
                                  onClose();
                                  closeEvent?.stopPropagation();
                                  onClose();
                                  setTimeout(() => setDisableClickAway(false), 500);
                                },
                                denyLabel: 'Cancel',
                                denyAction: (reason, closeEvent) => {
                                  closeEvent?.stopPropagation();
                                  setTimeout(() => setDisableClickAway(false), 500);
                                },
                              }),
                            ),
                          }),
                        ),
                      );
                    }
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  Clear All
                </Button>
              )}
            </Box>
          </Box>

          {/* Content Section */}
          <Box className={classes.content}>
            {!alerts.size && (
              <ZeroStateView
                qIllustration='notificationBellTwo'
                size='small'
                description="You don't have any notifications."
                button={
                  <IconButton
                    size='small'
                    tabIndex={0}
                    aria-label='keyboard hook'
                    autoFocus
                    onClick={(event: SyntheticEvent) => event.stopPropagation()}
                    sx={{ width: 0, height: 0, padding: 0 }}
                  />
                }
              />
            )}

            {Boolean(alerts.size) && (
              <List dense disablePadding>
                {alerts.valueSeq().map((alert: any, index: number) => (
                  <React.Fragment key={alert.id}>
                    {Boolean(index) && <Divider key={`${alert.id}-divider`} variant='inset' component='li' />}
                    <ListItem disablePadding key={`${alert.id}-item`}>
                      <ListItemButton
                        data-alertid={alert.id}
                        id={`${alert.type}:${index}`}
                        key={`${alert.id}-item-button`}
                        autoFocus={!index}
                        alignItems='flex-start'
                        onClick={(event) => {
                          event.stopPropagation();
                          if (alert.id && !alert.isRead) {
                            dispatch(alertsActions.updateAlert(alert.set('isRead', true)));
                          }
                          if (alert.deepLinkUri?.length) {
                            navigate(pathForAlert(alert));
                          }
                          onClose();
                        }}
                        className={classes.listItem}
                        ref={(itemRef) => {
                          if (itemRef) {
                            intersectionObserver?.observe(itemRef);
                          }
                        }}
                      >
                        <Dump obj={alert} />
                        <ListItemAvatar sx={{ minWidth: 42 }}>
                          <Avatar
                            variant='rounded'
                            alt={alert.title}
                            src={alert.icon}
                            sx={{
                              '& svg': {
                                filter: 'brightness(0) invert(1)', // make it white
                              },
                              width: 32,
                              height: 32,
                              padding: 0.8,
                              border: 0,
                              backgroundColor:
                                palette[alert.iconBgColor] ||
                                theme.palette.data.sequence[
                                  (alert.title?.charCodeAt(0) ?? 0) % theme.palette.data.sequence.length
                                ],
                            }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={alert.title}
                          secondary={<span dangerouslySetInnerHTML={{ __html: maskAmount(alert.message) }} />}
                          slotProps={{
                            primary: {
                              variant: 'paragraphMedium',
                            },
                            secondary: {
                              variant: 'captionRegular',
                            },
                          }}
                        />

                        {/* Status & action section */}
                        <Box minWidth={72} display='flex' justifyContent='flex-end' paddingY={2} paddingLeft={1}>
                          <Stack
                            gap={1}
                            className={classes.hideOnHover}
                            alignItems='center'
                            sx={{ transition: 'opacity 120ms ease', pointerEvents: 'none' }}
                          >
                            <Badge color='error' variant='dot' invisible={alert.isRead} />

                            <Typography variant='captionRegular' color='text.secondary'>
                              {getSmartTimeLabel(alert.createdAt)}
                            </Typography>
                          </Stack>
                          <Box
                            className={classes.showOnHover}
                            sx={{ transition: 'opacity 120ms ease, visibility 120ms ease' }}
                          >
                            <Tooltip title='Remove'>
                              <IconButton
                                size='small'
                                aria-label='delete'
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                  event.stopPropagation();
                                  dispatch(alertsActions.deleteAlert(alert.set('isDeleted', true)));
                                }}
                              >
                                <ClearIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Popover>
      )}
    </>
  );
});

NotificationsNavIcon.displayName = 'NotificationsNavIcon';

export default NotificationsNavIcon;
